import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// Helper function (can be moved to utils if used elsewhere)
const safeParseFloat = (value: string | number | undefined, defaultValue: number): number => {
    if (value === undefined || value === null) return defaultValue;
    const parsed = parseFloat(value.toString());
    return isNaN(parsed) ? defaultValue : parsed;
};

interface SliderContextValue {
    __id?: string;
    defaultValue?: string | number;
    sliderValuesMapper?: { [key: string]: { value: string; unit: string; }; };
    [key: string]: any; // Allow other properties
}

export function useSliderRangeInput(dataElementContext: SliderContextValue | null | undefined) {

    const sliderValuesMapper = useMemo(() => dataElementContext?.sliderValuesMapper || {}, [dataElementContext?.sliderValuesMapper]);

    const initialDefaultValue = dataElementContext?.defaultValue;
    const initialSliderValue = safeParseFloat(initialDefaultValue, 0);

    const [selectedValue, setSelectedValue] = useState(() => {
        const mapperEntry = sliderValuesMapper[initialSliderValue];
        return mapperEntry?.value || initialSliderValue.toString();
    });

    const [selectedUnit, setSelectedUnit] = useState(() => {
        const mapperEntry = sliderValuesMapper[initialSliderValue];
        const maxEntryKey = Object.keys(sliderValuesMapper).reduce((a, b) =>
            sliderValuesMapper[a]?.value > sliderValuesMapper[b]?.value ? a : b, '');
        const maxEntry = sliderValuesMapper[maxEntryKey];
        return mapperEntry?.unit || maxEntry?.unit || '';
    });

    const [sliderValue, setSliderValue] = useState<number>(initialSliderValue);

    const sliderInputRef = useRef<HTMLInputElement | null>(null);

    const updateRangeProgress = useCallback((input: HTMLInputElement) => {
        try {
            const min = safeParseFloat(input.min, 0);
            const max = safeParseFloat(input.max, 100);
            const value = safeParseFloat(input.value, min);
            const range = max - min;

            let percentage = 0;
            if (range > 0) {
                percentage = ((value - min) / range) * 100;
            }

            percentage = Math.max(0, Math.min(100, percentage));

            console.log('🎚️ updateRangeProgress:', { min, max, value, range, percentage });

            input.style.setProperty('--range-progress', `${percentage}%`);
        } catch (error) {
            console.error('Error updating range progress:', error);
        }
    }, []);

    const onChange = useCallback((newValue: number | Event) => {
        if (typeof newValue === 'object') {
            return;
        }

        const numValue = Number(newValue);
        if (isNaN(numValue)) {
            return;
        }

        setSliderValue(numValue);

        if (dataElementContext?.onSliderChange) {
            dataElementContext.onSliderChange(numValue);
        }
    }, [dataElementContext?.onSliderChange]);

    const handleInput = useCallback((event: Event) => {
        if (event.target instanceof HTMLInputElement) {
            const newValue = safeParseFloat(event.target.value, 0);

            updateRangeProgress(event.target);

            if (newValue !== sliderValue) {
                onChange(newValue);
            }
        }
    }, [updateRangeProgress, onChange, sliderValue, dataElementContext?.onSliderChange]);

    useEffect(() => {
        const newSliderValue = safeParseFloat(dataElementContext?.defaultValue, 0);

        if (newSliderValue !== sliderValue && dataElementContext?.defaultValue != null) {
            setSliderValue(newSliderValue);
        }
    }, [dataElementContext?.defaultValue, sliderValue]);

    useEffect(() => {
        const mapperEntry = sliderValuesMapper[sliderValue];
        const maxEntryKey = Object.keys(sliderValuesMapper).reduce((a, b) =>
            sliderValuesMapper[a]?.value > sliderValuesMapper[b]?.value ? a : b, '');
        const maxEntry = sliderValuesMapper[maxEntryKey];

        const newSelectedValue = mapperEntry?.value || sliderValue.toString();
        const newSelectedUnit = mapperEntry?.unit || maxEntry?.unit || '';

        setSelectedValue(newSelectedValue);
        setSelectedUnit(newSelectedUnit);

        if (sliderInputRef.current && sliderInputRef.current.value !== sliderValue.toString()) {
            sliderInputRef.current.value = sliderValue.toString();
            updateRangeProgress(sliderInputRef.current);
        }
    }, [sliderValue, sliderValuesMapper, updateRangeProgress]);

    useEffect(() => {
    }, [dataElementContext?.defaultValue, dataElementContext?.__id, sliderValue]);

    useEffect(() => {
        if (!dataElementContext?.__id) {
            return;
        }

        const selector = `.slider-input[type="range"][name="slider-range-${dataElementContext.__id}"]`;
        const rangeInputs = document.querySelectorAll(selector);
        const firstRangeInput = rangeInputs.length > 0 ? rangeInputs[0] as HTMLInputElement : null;

        if (firstRangeInput) {
            sliderInputRef.current = firstRangeInput;

            const currentInputElementValue = safeParseFloat(firstRangeInput.value, 0);
            if (initialSliderValue !== currentInputElementValue) {
                firstRangeInput.value = initialSliderValue.toString();
            }

            updateRangeProgress(firstRangeInput);

            firstRangeInput.addEventListener('input', handleInput);
            firstRangeInput.addEventListener('change', handleInput);

            return () => {
                firstRangeInput.removeEventListener('input', handleInput);
                firstRangeInput.removeEventListener('change', handleInput);
                sliderInputRef.current = null;
            };
        }
    }, [dataElementContext?.__id, initialSliderValue, handleInput, updateRangeProgress]);

    useEffect(() => {
        if (sliderInputRef.current) {
            const currentInputElementValue = safeParseFloat(sliderInputRef.current.value, 0);
            if (currentInputElementValue !== sliderValue) {
                sliderInputRef.current.value = sliderValue.toString();
                updateRangeProgress(sliderInputRef.current);
            }
        }
    }, [sliderValue, updateRangeProgress]);

    const contextValue = useMemo(() => {
        const labels = sliderValuesMapper ? Object.values(sliderValuesMapper) : [];
        return {
            ...dataElementContext,
            selectedValue,
            selectedUnit,
            sliderValue,
            labels,
            handleInput,
            onChange,
        };
    }, [
        selectedValue,
        selectedUnit,
        sliderValue,
        dataElementContext,
        sliderValuesMapper,
        handleInput,
        onChange,
    ]);

    return contextValue;
}

export default useSliderRangeInput;
